import { Button, ButtonList, Table, TextAlign, Tray } from '@busybusy/webapp-react-ui';
import {
  OrganizationCustomFields,
  OrganizationCustomFieldsTypes,
  OrganizationCustomFieldsUpdateInput,
} from '__generated__/graphql';
import classNames from 'classnames';
import { EmptyState, Header, HeaderDialog, MoreButton, Panel } from 'components';
import CreateEditCustomFieldDialog from 'components/foundation/dialogs/CreateEditCustomFieldsDialog/CreateEditCustomFieldsDialog';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { useActiveMember, useAxios, useOpenable } from 'hooks';
import useOrganizationCustomFields from 'hooks/models/organization-custom-fields/useOrganizationCustomFields';
import useOnMount from 'hooks/utils/useOnMount/useOnMount';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { useState } from 'react';
import { ClassName } from 'types/ClassName';
import { extractCustomFields, ICustomFieldTableRow, parseTypeString } from 'utils/organizationCustomFieldsUtils';
import { isAdmin } from 'utils/positionUtils';
import './CustomFieldsDialog.scss';
import deleteCustomFieldRpcService from 'services/rpc/deleteCustomFieldRpcService';

export interface ICustomFieldsDialogProps {
  className?: ClassName;
  type: OrganizationCustomFieldsTypes;
  isOpen: boolean;
  onClose: () => void;
}

const CustomFieldsDialog = (props: ICustomFieldsDialogProps) => {
  const { className, type, isOpen, onClose } = props;

  const { getOrganizationCustomFields, updateOrganizationCustomFields } = useOrganizationCustomFields();
  const activeMember = useActiveMember();
  const memberIsAdmin = isAdmin(activeMember.position);
  const axiosClient = useAxios();

  const [customFields, setCustomFields] = useState<OrganizationCustomFields | null>(null);
  const [selectedRow, setSelectedRow] = useState<ICustomFieldTableRow | null>(null);
  const renderCustomFields = customFields ? extractCustomFields(customFields) : [];
  const filteredCustomFields = renderCustomFields.filter((field) => memberIsAdmin || !field.adminOnly);

  const createEditDialog = useOpenable();
  const overLimitDialog = useOpenable();
  const deleteConfirmationDialog = useOpenable();

  useOnMount(() => {
    getCustomFields();
  });

  const getCustomFields = async () => {
    const organizationCustomFields = await getOrganizationCustomFields(type);
    if (organizationCustomFields) {
      setCustomFields(organizationCustomFields);
    }
  };

  const handleCreateClick = () => {
    if (renderCustomFields.length < 5) {
      createEditDialog.open();
    } else {
      overLimitDialog.open();
    }
  };

  const handleRowClick = (row: ICustomFieldTableRow) => {
    setSelectedRow(row);
    createEditDialog.open();
  };

  const handleCreateEditClose = () => {
    setSelectedRow(null);
    createEditDialog.close();
    getCustomFields();
  };

  const handleDeleteConfirmationDialogClose = () => {
    deleteConfirmationDialog.close();
    setSelectedRow(null);
  };

  const deleteClicked = () => {
    if (selectedRow && customFields) {
      const fieldIndex = [1, 2, 3, 4, 5].findIndex(
        (i) => customFields[`title${i}` as keyof OrganizationCustomFields] === selectedRow.title
      );
      if (fieldIndex !== -1) {
        handleDelete(fieldIndex);
      }
    }
  };
  const handleDelete = (fieldIndex: number) => {
    deleteConfirmationDialog.close();
    if (!isNil(customFields)) {
      const fieldKeys = ['title1', 'title2', 'title3', 'title4', 'title5'] as const;
      const adminOnlyKeys = ['adminOnly1', 'adminOnly2', 'adminOnly3', 'adminOnly4', 'adminOnly5'] as const;
      const subtitleKeys = ['subtitle1', 'subtitle2', 'subtitle3', 'subtitle4', 'subtitle5'] as const;

      let input: OrganizationCustomFieldsUpdateInput = {
        id: customFields.id,
      };

      input = {
        id: customFields.id,
        ...fieldKeys.reduce((acc, key, index) => {
          acc[key] = fieldIndex === index ? null : customFields[key];
          acc[adminOnlyKeys[index]] = fieldIndex === index ? false : customFields[adminOnlyKeys[index]];
          acc[subtitleKeys[index]] = fieldIndex === index ? null : customFields[subtitleKeys[index]];
          return acc;
        }, {} as Partial<OrganizationCustomFieldsUpdateInput>),
      };

      Promise.all([updateOrganizationCustomFields(input), deleteCustomFieldRpcService(axiosClient).deleteField(type, fieldIndex)]).then(() => {
        handleDeleteConfirmationDialogClose();
        getCustomFields();
      });
    }
  };

  const classes = classNames('custom-fields-dialog', className);

  const tableColumns = [
    {
      key: 'label',
      title: t('Label'),
      cell: (row: ICustomFieldTableRow) => renderLabelCell(row),
      align: TextAlign.LEFT,
      cellClassName: 'p-5',
    },
    {
      key: 'subtitle',
      title: t('Secondary Label'),
      align: TextAlign.LEFT,
      cellClassName: 'p-5',
    },
    {
      key: 'adminOnly',
      cell: (row: ICustomFieldTableRow) => renderAdminOnlyCell(row),
      title: t('Admin Only'),
      align: TextAlign.LEFT,
      cellClassName: 'p-5',
    },
  ];

  const renderLabelCell = (row: ICustomFieldTableRow) => {
    const renderDropDown = (closeDropdown: () => void) => {
      const handleDelete = (event: React.MouseEvent) => {
        closeDropdown();
        event.stopPropagation();
        event.preventDefault();

        setSelectedRow(row);
        deleteConfirmationDialog.open();
      };

      return (
        <ButtonList>
          <Button onClick={handleDelete}>{t('Delete')}</Button>
        </ButtonList>
      );
    };
    return (
      <Tray align="right">
        <MoreButton renderContent={renderDropDown} />
        <div className={'text-sm font-bold'}>{row.title}</div>
      </Tray>
    );
  };

  const renderAdminOnlyCell = (row: ICustomFieldTableRow) => {
    return <span>{row.adminOnly ? t('Yes') : t('No')}</span>;
  };
  return (
    <HeaderDialog
      size={'full'}
      isOpen={isOpen}
      onClose={onClose}
      className={classes}
      title={t(`Custom ${parseTypeString(type)} Fields`)}
      showBackArrowAsClose={true}
    >
      <div>
        <Panel>
          <Header>
            <Button type={'secondary'} onClick={handleCreateClick}>
              {t('Create')}
            </Button>
          </Header>
          <div className={'m-5'}>
            {renderCustomFields && renderCustomFields.length > 0 ? (
              <div className="p-10">
                <Table
                  cols={tableColumns}
                  data={filteredCustomFields}
                  className={'custom-fields-table'}
                  onRowClick={(row) => {
                    handleRowClick(row);
                  }}
                />
              </div>
            ) : (
              <EmptyState
                title={t('No Custom Fields')}
                subtitle={t('Use the Create button above to get started. You may add up to 5 custom fields.')}
              />
            )}
          </div>
        </Panel>
      </div>
      <CreateEditCustomFieldDialog
        isOpen={createEditDialog.isOpen}
        onClose={handleCreateEditClose}
        organizationCustomFields={customFields}
        selectedRow={selectedRow}
        onDelete={handleDelete}
        type={type}
      />
      <HeaderDialog
        isOpen={overLimitDialog.isOpen}
        onClose={overLimitDialog.close}
        title={t('Limit Reached')}
        className={'custom-fields-dialog'}
        divider={false}
      >
        <div className={'p-5'}>
          <div>
            {t(
              'You can have a maximum of 5 custom fields. You will need to delete one of your existing fields in order to continue.'
            )}
          </div>
          <Button type={'secondary'} className={'mt-5'} onClick={overLimitDialog.close}>
            {t('Close')}
          </Button>
        </div>
      </HeaderDialog>
      {selectedRow && (
        <DeleteConfirmationDialog
          isOpen={deleteConfirmationDialog.isOpen}
          onClose={handleDeleteConfirmationDialogClose}
          onDelete={deleteClicked}
          title={t('Delete Custom Field')}
        >
          {t(
            'This action can not be undone. Any data entered in these fields will be permanently erased. Are you sure you want to continue?'
          )}
        </DeleteConfirmationDialog>
      )}
    </HeaderDialog>
  );
};

export default CustomFieldsDialog;
